import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { SectionItem, SectionWrapped } from './TopBarDropdownSectionItem.component'
import { Stack, Typography } from '@mui/material'
import { getClientHelpCenterLink } from 'utils/localization'

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import AddHomeOutlinedIcon from '@mui/icons-material/AddHomeOutlined'
import { AnalyticsEvent } from 'utils/analytics'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { Fragment } from 'react'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import { MUIDivider } from 'components/common/MUIDivider'
import { Path } from 'constants/router'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import type { TopbarProps } from './TopBarDropdown.component'
import { externalNavigate } from 'utils/helpers'
import i18n from 'translations/i18n'
import { useAppMeta } from 'components/higher-order/App/appMeta.context'
import { useAuth0 } from 'utils/auth'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspaceRoles } from 'utils/hooks'

/**
 * @component TopBarDropdownClientContent - Client-specific menu items for the TopBar dropdown
 */
export const TopBarDropdownClientContent: React.FC<TopbarProps> = ({ setIsContactUsOpen, closeMenuHandler, handleLogout, handleLogAnalyticsEvent }) => {
  const { t } = useTranslation(['topbar', 'authentication'])
  const navigate = useNavigate()
  const { isUserSet } = useAppMeta()
  const { user } = useAuth0()
  const { clientData, currentUserWorkspace } = useUserData()
  const { isWorkspaceAdmin, isWorkspaceMember, isWorkspaceSponsored, isRestrictedMember } = useWorkspaceRoles()


  // Restricted members must not see the Members entry (their only workspace-section item), so the
  // whole section is hidden for them (ISSUE-2488). Restricted Admins keep it (isRestrictedMember is false).
  const showWorkspaceSection = !!currentUserWorkspace && !isWorkspaceSponsored && !isRestrictedMember

  return (
    <Fragment>
      <SectionWrapped>
        <Typography variant="text-xs" fontWeight={450} color={GRAY_700} padding="0 .4rem">
          {t('topbar:your_account')}
        </Typography>

        {isUserSet && (
          <SectionItem
            label={t('topbar:profile')}
            icon={<AccountCircleOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
            onClick={(e) => {
              navigate(Path.PROFILE)
              handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_PROFILE)
              closeMenuHandler(e)
            }}
          />
        )}

      </SectionWrapped>

      {/* Workspace section */}
      {showWorkspaceSection && (
        <Stack paddingTop=".8rem" width="100%">
          <SectionWrapped>
            <Typography variant="text-xs" fontWeight={450} color={GRAY_700} padding="0 .4rem">
              {t('topbar:workspace_section')}
            </Typography>

            {isWorkspaceAdmin && (
              <SectionItem
                label={t('topbar:settings')}
                icon={<SettingsOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
                onClick={(e) => {
                  handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_SETTINGS)
                  navigate(Path.WORKSPACE_SETTINGS_WORKSPACE)
                  closeMenuHandler(e)
                }}
              />
            )}

            {isWorkspaceAdmin && (
              <SectionItem
                label={t('topbar:workspace_profile')}
                icon={<AddHomeOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
                onClick={(e) => {
                  handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_WORKSPACE_PROFILE)
                  navigate(Path.WORKSPACE_SETTINGS_TAB.replace(':tab', 'plan'))
                  closeMenuHandler(e)
                }}
              />
            )}

            {(isWorkspaceAdmin || isWorkspaceMember) && (
              <SectionItem
                label={t('topbar:members')}
                icon={<PeopleOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
                onClick={(e) => {
                  handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_MEMBERS)
                  navigate(Path.WORKSPACE_SETTINGS_MEMBERS)
                  closeMenuHandler(e)
                }}
              />
            )}

          </SectionWrapped>
        </Stack>
      )}

      <MUIDivider margin={12} sx={{ width: '100%' }} />

      <SectionWrapped>
        {isUserSet && (
          <SectionItem
            isLight
            isExternalLink
            label={t('topbar:help_center')}
            icon={<HelpOutlineOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
            onClick={() => {
              externalNavigate(getClientHelpCenterLink(i18n.language))
              handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_HELP_CENTER)
            }}
          />
        )}

        <SectionItem
          isLight
          label={t('topbar:terms_and_privacy')}
          icon={<DescriptionOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
          onClick={() => {
            navigate(Path.TERMS_AND_PRIVACY)
            handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_TERMS_AND_POLICY)
          }}
        />

        {isUserSet && (
          <SectionItem
            isLight
            label={t('topbar:contact_us')}
            icon={<PhoneOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
            onClick={() => {
              handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_CONTACT_US)
              setIsContactUsOpen()
            }}
          />
        )}
      </SectionWrapped>

      <MUIDivider margin={12} sx={{ width: '100%' }} />

      <SectionWrapped>
        <Stack
          gap=".2rem"
          padding="0 .4rem"
          sx={{ cursor: 'pointer' }}
          onClick={() => {
            navigate(Path.PROFILE)
            handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_USER_PROFILE)
          }}
        >
          <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
            {clientData?.name ?? user?.name}
          </Typography>

          <Typography variant="text-xs" fontWeight={350} color={GRAY_700}>
            {user?.email}
          </Typography>
        </Stack>

        <SectionItem
          isLight
          label={t('authentication:log_out')}
          icon={<LogoutOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
          onClick={(e) => {
            handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_LOG_OUT)
            handleLogout(e)
          }}
        />
      </SectionWrapped>
    </Fragment>
  )
}
